import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { InfoIcon } from 'lucide-react';

import { Label } from 'components/Label';
import { MultiSelect } from 'components/MultiSelect';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { getQuickQuoteCustomAccessorials } from 'lib/api/getQuickQuoteCustomAccessorials';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { QuickQuoteInputs, QuoteMode } from '../types';

export const CustomAccessorialsSection = () => {
  const { control } = useFormContext<QuickQuoteInputs>();
  const { availableCustomAccessorials, setavailableCustomAccessorials } =
    useQuickQuoteContext();
  const [isLoadingAccessorials, setIsLoadingAccessorials] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchAccessorials = async () => {
      setIsLoadingAccessorials(true);
      const result = await getQuickQuoteCustomAccessorials(QuoteMode.TL);

      if (!cancelled && result.isOk()) {
        setavailableCustomAccessorials(result.value.accessorials);
      }

      if (!cancelled) {
        setIsLoadingAccessorials(false);
      }
    };

    void fetchAccessorials();

    return () => {
      cancelled = true;
    };
  }, [setavailableCustomAccessorials]);

  return (
    availableCustomAccessorials.length > 0 && (
      <Flex direction='col' gap='sm' className='w-full mx-0'>
        <Flex align='center' justify='between' className='w-full'>
          <Label name='customAccessorials' hideAIHint required={false}>
            Accessorials
          </Label>
          <Tooltip delayDuration={10}>
            <TooltipTrigger asChild>
              <InfoIcon className='w-4 h-4' />
            </TooltipTrigger>
            <TooltipContent className='max-w-60 mr-2 px-3 py-2'>
              <Typography variant='body-xs' className='text-neutral-700'>
                Notes and charges associated to accessorials added here will
                appear below on your quote summary for calculation purposes.
              </Typography>
            </TooltipContent>
          </Tooltip>
        </Flex>

        <Controller
          name='customAccessorials'
          control={control}
          render={({ field }) => (
            <MultiSelect
              placeholder='Select accessorials...'
              value={field.value ?? []}
              onChange={field.onChange}
              options={availableCustomAccessorials.map((accessorial) => ({
                label: accessorial.name,
                value: accessorial.name,
              }))}
              className='w-full h-auto! min-h-8 [&_span.inline-flex]:bg-brand-100'
              disabled={isLoadingAccessorials}
            />
          )}
        />
      </Flex>
    )
  );
};
